import { useCallback, useMemo } from "react";
import { FolioWiseHolding } from "creso-core/types";
import { useGetHoldingFolios } from "creso-core/hooks";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
  Badge,
  cn,
} from "web-design-system";

interface Props {
  onChange?: (holdingScheme: FolioWiseHolding) => void;
  investmentAccountGuid: string;
  schemeIsin?: string;
  value: FolioWiseHolding | null;
  filter?: (folio: FolioWiseHolding) => boolean;
}

export function HoldingFoliosDropdown(props: Props) {
  const { investmentAccountGuid, schemeIsin = "", onChange } = props;

  const { data: holdingFolios = [] } = useGetHoldingFolios(
    investmentAccountGuid,
    schemeIsin,
    !!schemeIsin
  );

  const options = useMemo(() => {
    const filteredHoldingFolios = props.filter
      ? holdingFolios.filter(props.filter)
      : holdingFolios;
    return filteredHoldingFolios.map((d) => ({
      label: d.folio_number,
      value: d.folio_id.toString(),
      internal_flag: d.internal_flag,
    }));
  }, [holdingFolios, props.filter]);

  const onChangeHandler = useCallback(
    (v: string) => {
      const selectedOption = holdingFolios?.find(
        (d) => d.folio_id.toString() === v
      );

      if (selectedOption) {
        onChange?.(selectedOption);
      }
    },
    [holdingFolios, onChange]
  );

  const value = props.value?.folio_id.toString() ?? "";

  return (
    <Select value={value} onValueChange={(v) => onChangeHandler(v)}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Select folio number" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            className="w-full flex-1"
            key={option.value}
            value={option.value}
          >
            <div className="flex flex-row items-center justify-between gap-1">
              <span>{option.label}</span>
              <Badge
                variant="custom"
                className={cn(
                  option.internal_flag
                    ? "bg-tag-slate text-tag-slate-foreground"
                    : "bg-tag-yellow text-tag-yellow-foreground",
                  "text-xs"
                )}
              >
                {option.internal_flag ? "Internal" : "External"}
              </Badge>
            </div>
          </SelectItem>
        ))}
        {options.length === 0 && (
          <div className="p-2 text-xs text-muted-foreground">
            No options found
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
